"use client";

import React, { useState, useEffect } from "react";
import Link from "next/link";
import { X, ChevronDown, ChevronUp, User2, ShoppingCart, Truck, LogOut } from "lucide-react";
import { useCurrency } from "@/packages/context/CurrencyContext";
import { useAuth } from "@/packages/context/AuthContext";
import { useFastRouter } from "@/packages/hooks/useFastRouter";
import { useSearchParams } from "next/navigation";
import LogoV2 from "./LogoV2";
import { HEADER_V2_DESIGN_SYSTEM } from "./designTokens";
import SelectField from "@/components/common/SelectBox";
import CountryCurrencySelector from "./CountryCurrencySelector";
import { MenuNode } from "./menuAdapter";

interface SideMenuV2Props {
  categories: MenuNode[];
  loadingCategories?: boolean;
  isOpen: boolean;
  onClose: () => void;
}

export default function SideMenuV2({ categories, loadingCategories = false, isOpen, onClose }: SideMenuV2Props) {
  const { code: currencyCode, changeCurrency, currencies: currencyOptions } = useCurrency();
  const [openRootCatId, setOpenRootCatId] = useState<string | null>(null);
  const [openSubCatId, setOpenSubCatId] = useState<string | null>(null);

  const { isLoggedIn, logout } = useAuth();
  const { fastPush } = useFastRouter();
  const searchParams = useSearchParams();
  const { typography, colors } = HEADER_V2_DESIGN_SYSTEM;

  // 1. Lock screen scrolling when drawer is open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const toggleRootCat = (catId: string) => {
    setOpenRootCatId(openRootCatId === catId ? null : catId);
    setOpenSubCatId(null); // Close any nested subcategory when changing roots
  };

  const toggleSubCat = (subCatId: string, e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent toggling the parent root category
    setOpenSubCatId(openSubCatId === subCatId ? null : subCatId);
  };

  const handleCurrencyChange = (val: string) => {
    const selected = currencyOptions.find((c) => c.code === val);
    if (selected) {
      changeCurrency({
        id: selected.id,
        symbol: selected.symbol,
        code: selected.code,
        exchangeRate: selected.exchangeRate,
      });

      const params = new URLSearchParams(searchParams.toString());
      params.set("currency", val);
      fastPush(`?${params.toString()}`);
    }
  };

  return (
    <div className={`fixed inset-0 z-[45] flex ${typography.fontFamilySans}`}>
      {/* Dark backdrop overlay */}
      <div 
        className="absolute inset-0 bg-black/40 backdrop-blur-xs transition-opacity duration-300"
        onClick={onClose}
      />

      {/* Accordion Side Drawer Container */}
      <div className={`relative w-[300px] max-w-[85vw] ${colors.whiteBg} h-full shadow-2xl flex flex-col z-[46] animate-slide-in-left`}>
        
        {/* Drawer Header row (Logo left, Close trigger right) */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
          <LogoV2 />
          <button 
            onClick={onClose}
            aria-label="Close menu"
            className="p-1.5 rounded-full hover:bg-slate-50 text-slate-500 transition-colors duration-150 cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Dynamic Accordion Body List */}
        <div className="flex-1 overflow-y-auto px-5 py-4">
          {loadingCategories ? (
            <div className="text-slate-400 text-xs py-4 font-medium">Loading menu categories...</div>
          ) : categories.length === 0 ? (
            <div className="text-slate-400 text-xs py-4 font-medium">No categories found</div>
          ) : (
            <nav className="flex flex-col gap-1 text-slate-800 text-sm font-semibold">
              {categories.map((rootCat) => {



                const hasSubCategories = rootCat.children && rootCat.children.length > 0;
                const isRootOpen = openRootCatId === rootCat.id;

                return (
                  <div key={rootCat.id} className="border-b border-slate-50/60 pb-1">
                    {/* Level 0 Row */}
                    {hasSubCategories ? (
                      <div className="flex items-center justify-between w-full rounded-md hover:bg-slate-50/50 transition-colors duration-150">
                        {/* Title: Navigate directly on click */}
                        <Link
                          href={rootCat.href}
                          onClick={onClose}
                          className={`flex-1 py-2.5 px-2 capitalize font-semibold transition-colors duration-150 ${
                            isRootOpen ? "text-[var(--brand-accent)]" : "text-slate-700 hover:text-[var(--brand-accent)]"
                          }`}
                        >
                          {rootCat.label}
                        </Link>
                        {/* Chevron: Toggles collapse/expand only */}
                        <button
                          onClick={() => toggleRootCat(rootCat.id)}
                          className="p-2.5 text-slate-400 hover:text-[var(--brand-accent)] cursor-pointer flex items-center justify-center border-l border-slate-100/50"
                          aria-label={`Toggle ${rootCat.label} subcategories`}
                        >
                          {isRootOpen ? (
                            <ChevronUp className="w-4 h-4 text-[var(--brand-accent)]" />
                          ) : (
                            <ChevronDown className="w-4 h-4" />
                          )}
                        </button>
                      </div>
                    ) : (
                      <Link
                        href={rootCat.href}
                        onClick={onClose}
                        className="block py-2.5 px-2 text-slate-700 hover:text-[var(--brand-accent)] hover:bg-slate-50/50 rounded-md transition-all duration-150 capitalize font-semibold"
                      >
                        {rootCat.label}
                      </Link>
                    )}

                    {/* Level 1 Subcategories List */}
                    {isRootOpen && hasSubCategories && (
                      <div className="pl-4 mt-1 flex flex-col gap-0.5 border-l border-slate-100 ml-3">
                        {rootCat.children!.map((subCat) => {
                          const hasGrandchildren = subCat.children && subCat.children.length > 0;
                          const isSubOpen = openSubCatId === subCat.id;

                          return (
                            <div key={subCat.id}>
                              {/* Level 1 Row */}
                              {hasGrandchildren ? (
                                <div className="flex items-center justify-between w-full rounded-md hover:bg-slate-50/50 transition-colors duration-150">
                                  {/* Title: Navigate directly on click */}
                                  <Link
                                    href={subCat.href}
                                    onClick={onClose}
                                    className={`flex-1 py-2 px-2 text-[13px] font-semibold capitalize transition-colors duration-150 ${
                                      isSubOpen ? "text-[var(--brand-accent)]" : "text-slate-650 hover:text-[var(--brand-accent)]"
                                    }`}
                                  >
                                    {subCat.label}
                                  </Link>
                                  {/* Chevron: Toggles collapse/expand only */}
                                  <button
                                    onClick={(e) => toggleSubCat(subCat.id, e)}
                                    className="p-2 text-slate-400 hover:text-[var(--brand-accent)] cursor-pointer flex items-center justify-center border-l border-slate-100/50"
                                    aria-label={`Toggle ${subCat.label} grandchild categories`}
                                  >
                                    {isSubOpen ? (
                                      <ChevronUp className="w-3.5 h-3.5 text-[var(--brand-accent)]" />
                                    ) : (
                                      <ChevronDown className="w-3.5 h-3.5" />
                                    )}
                                  </button>
                                </div>
                              ) : (
                                <Link
                                  href={subCat.href}
                                  onClick={onClose}
                                  className="block py-2 px-2 text-[13px] font-semibold text-slate-650 hover:text-[var(--brand-accent)] hover:bg-slate-50/50 rounded-md transition-all duration-150 capitalize"
                                >
                                  {subCat.label}
                                </Link>
                              )}

                              {/* Level 2 Grandchildren List */}
                              {isSubOpen && hasGrandchildren && (
                                <div className="pl-4 mt-1 flex flex-col gap-0.5 border-l border-slate-100 ml-2">
                                  {subCat.children!.map((grandchild) => (
                                    <Link
                                      key={grandchild.id}
                                      href={grandchild.href}
                                      onClick={onClose}
                                      className="block py-1.5 px-2 text-[12px] font-normal text-slate-500 hover:text-[var(--brand-accent)] hover:bg-slate-50/30 rounded-md transition-all duration-150 capitalize"
                                    >
                                      {grandchild.label}
                                    </Link>
                                  ))}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Static Deals & Offers Link */}
              <div className="border-b border-slate-50/60 pb-1">
                <Link
                  href="/crazy-deals"
                  onClick={onClose}
                  className="block py-2.5 px-2 text-slate-700 hover:text-[var(--brand-accent)] hover:bg-slate-50/50 rounded-md transition-all duration-150 capitalize font-semibold"
                >
                  Deals &amp; Offers
                </Link>
              </div>

              {/* Static Contact Us Link */}
              <div className="border-b border-slate-50/60 pb-1">
                <Link
                  href="/contact"
                  onClick={onClose}
                  className="block py-2.5 px-2 text-slate-700 hover:text-[var(--brand-accent)] hover:bg-slate-50/50 rounded-md transition-all duration-150 capitalize font-semibold"
                >
                  Contact Us
                </Link>
              </div>
            </nav>

          )}

        </div>

        {/* Mobile Drawer Footer Controls */}
        <div className="border-t border-slate-100 p-5 flex flex-col gap-4 text-xs font-semibold text-slate-700 bg-slate-50/50">
          
          {/* Country & Currency Selector */}
          <div className="flex items-center">
            <CountryCurrencySelector className="flex" />
          </div>

          {/* Currency Selector (Hidden UI) */}
          <div className="w-[90px] hidden">
            <SelectField
              name="currency"
              value={currencyCode}
              onChange={handleCurrencyChange}
              options={currencyOptions.map((c) => ({
                label: `${c.symbol} ${c.code}`,
                value: c.code,
              }))}
            />
          </div>

          {/* Store Location */}

          {/* Shipping Policy */}
          <button
            onClick={() => {
              fastPush("/shipping-policy");
              onClose();
            }}
            className="flex items-center gap-2.5 text-left text-[13px] hover:text-[var(--brand-accent)] transition-colors duration-150 py-0.5 cursor-pointer text-slate-700 font-semibold"
          >
            <Truck className="w-4 h-4 text-slate-400" />
            <span>Shipping Policy</span>
          </button>

          {/* Account Profile / Login */}
          <button
            onClick={() => {
              if (isLoggedIn) fastPush("/user-profile");
              else fastPush("/sign-in");
              onClose();
            }}
            className="flex items-center gap-2.5 text-left text-[13px] hover:text-[var(--brand-accent)] transition-colors duration-150 py-0.5 cursor-pointer text-slate-700 font-semibold"
          >
            <User2 className="w-4 h-4 text-slate-400" />
            <span>{isLoggedIn ? "My Profile" : "Login / Register"}</span>
          </button>

          {isLoggedIn && (
            <button
              onClick={() => {
                logout();
                onClose();
              }}
              className="flex items-center gap-2.5 text-left text-[13px] text-rose-600 hover:text-rose-700 transition-colors duration-150 py-0.5 cursor-pointer font-semibold"
            >
              <LogOut className="w-4 h-4 text-rose-500" />
              <span>Logout</span>
            </button>
          )}

          {/* View Cart */}
          <button
            onClick={() => {
              fastPush("/cart");
              onClose();
            }}
            className="flex items-center gap-2.5 text-left text-[13px] hover:text-[var(--brand-accent)] transition-colors duration-150 py-0.5 cursor-pointer text-slate-700 font-semibold"
          >
            <ShoppingCart className="w-4 h-4 text-slate-400" />
            <span>View Shopping Cart</span>
          </button>

        </div>

      </div>
    </div>
  );
}
